import React, { useMemo } from "react";
import { ArrowRight, ExternalLink, Radar } from "lucide-react";
import type { RadarData, SeoMarket } from "./types";
import { fmtDateTime } from "./engineFormat";

interface Props {
  market: SeoMarket;
  radar: RadarData;
}

const CATEGORY_LABEL: Record<string, string> = {
  belopp: "Beløb", recension: "Anmeldelse", samla: "Samlelån", guide: "Guide", ranta: "Rente", "utan-uc": "Uden UC",
  betalningsanmarkning: "Betalingsanm.", smalan: "Smålån", snabblan: "Hurtiglån", kalkylator: "Beregner", ovrigt: "Øvrigt",
};

const brandName = (slug: string) => slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export default function CompetitorRadarPanel({ market, radar }: Props) {
  const newPagesByCompetitor = useMemo(() => {
    const groups = new Map<string, RadarData["newPages"]>();
    radar.newPages.forEach((p) => groups.set(p.competitor, [...(groups.get(p.competitor) || []), p]));
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [radar.newPages]);

  const reviewGaps = radar.reviewCoverage.filter((r) => !r.ourUrl && !r.contentId && Object.keys(r.coverage).length > 0);
  const missingTiers = radar.amountTiers.filter((t) => !t.ours && t.competitors >= 2);

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-1">
        <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
          <Radar className="w-4 h-4 text-[#0F3F34]" />
          <span>Konkurrent-radar ({market})</span>
        </h3>
        <p className="text-xs text-gray-500">
          Sitemaps scannes ugentligt, sammenligningssider dagligt. Rækkefølgen af partnere afslører, hvem netværkene
          betaler bedst lige nu.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 text-xs">
          {radar.competitors.filter((c) => c.active).slice(0, 8).map((c) => (
            <div key={c.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="font-bold text-gray-900 truncate">{c.name}</div>
              <div className="text-gray-500">{c.pages.toLocaleString("da-DK")} sider</div>
              <div className={c.newPages7d > 0 ? "text-[#0F3F34] font-semibold" : "text-gray-400"}>
                +{c.newPages7d} på 7 dage
              </div>
              {c.lastError && <div className="text-amber-700 truncate" title={c.lastError}>Scan-fejl</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Partner order on watched comparison pages */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h4 className="font-bold text-gray-900 text-sm">Partnerrækkefølge hos konkurrenter</h4>
          {radar.offers.length === 0 && <p className="text-xs text-gray-500">Ingen overvågede sammenligningssider endnu.</p>}
          {radar.offers.map((o) => (
            <div key={o.watchPageId} className="p-3 rounded-xl border border-gray-100 space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <a href={o.url} target="_blank" rel="noreferrer" className="font-bold text-gray-900 hover:underline truncate flex items-center gap-1">
                  {o.competitor} · {o.url.replace(/^https:\/\/(www\.)?/, "")} <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
                <span className="text-gray-400 shrink-0">{fmtDateTime(o.checkedAt)}</span>
              </div>
              {o.top3.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {o.top3.map((b, i) => (
                    <span key={b} className={`px-2 py-0.5 rounded-md border ${o.previousTop3[i] && o.previousTop3[i] !== b
                      ? "border-amber-300 bg-amber-50 text-amber-900" : "border-gray-200 bg-gray-50 text-gray-800"}`}>
                      #{i + 1} {brandName(b)}
                      {o.placements[i]?.rate_text ? <span className="text-gray-500"> · {o.placements[i].rate_text}</span> : null}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500">{o.lastStatus === "ok" ? "Ingen partnere fundet på siden" : o.lastStatus || "Ikke tjekket endnu"}</div>
              )}
              {o.changed && o.changeSummary && (
                <div className="text-amber-800 flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" /> {o.changeSummary}
                </div>
              )}
              {o.parseMethod && o.parseMethod !== "affiliate_links" && (
                <div className="text-gray-400">Aflæst via {o.parseMethod === "logo_alts" ? "logoer" : "tekstomtaler"} – tjek manuelt</div>
              )}
            </div>
          ))}
        </div>

        {/* New pages */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <h4 className="font-bold text-gray-900 text-sm">Nye sider de sidste 14 dage</h4>
          {newPagesByCompetitor.length === 0 && <p className="text-xs text-gray-500">Ingen nye sider siden første scan.</p>}
          {newPagesByCompetitor.map(([competitor, pages]) => (
            <div key={competitor} className="space-y-1.5 text-xs">
              <div className="font-bold text-gray-900">{competitor} · {pages.length}</div>
              <ul className="space-y-1">
                {pages.slice(0, 8).map((p) => (
                  <li key={p.url} className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] shrink-0">
                      {CATEGORY_LABEL[p.category || "ovrigt"] || p.category}
                    </span>
                    <a href={p.url} target="_blank" rel="noreferrer" className="font-mono text-gray-600 hover:underline truncate">{p.path}</a>
                  </li>
                ))}
                {pages.length > 8 && <li className="text-gray-400">+{pages.length - 8} flere</li>}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Review coverage */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-3">
          <h4 className="font-bold text-gray-900 text-sm">Anmeldelses-søgninger (bunden af tragten)</h4>
          <table className="w-full text-xs">
            <thead className="text-gray-400 text-left">
              <tr><th className="py-1.5">Brand</th><th>Bedste konkurrent</th><th>Vores side</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {radar.reviewCoverage.map((r) => (
                <tr key={r.brandSlug}>
                  <td className="py-1.5 font-semibold text-gray-900">
                    {r.brand}{r.isPartner && <span className="ml-1.5 text-[10px] font-normal text-emerald-700">partner</span>}
                  </td>
                  <td className="text-gray-600">{r.bestCompetitorPosition ? `#${r.bestCompetitorPosition}` : "–"}</td>
                  <td className={r.ourUrl || r.contentId ? "text-gray-700" : "text-amber-700"}>
                    {r.ourUrl ? "Ranker" : r.contentId ? `I køen (${r.contentStatus})` : "Mangler"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reviewGaps.length > 0 && (
            <p className="text-[11px] text-gray-500">
              {reviewGaps.length} huller er lagt i content-køen som idéer. Anmeldelser må kun bygge på dokumenterede vilkår –
              ingen opdigtede kundeoplevelser – og skal oplyse om provision.
            </p>
          )}
        </div>

        {/* Backlinks and amount tiers */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="space-y-2">
            <h4 className="font-bold text-gray-900 text-sm">Nye henvisende domæner hos konkurrenter</h4>
            {radar.backlinks.length === 0 ? (
              <p className="text-xs text-gray-500">Ingen data endnu (DataForSEO Backlinks API skal være aktiveret).</p>
            ) : (
              <ul className="text-xs space-y-1">
                {radar.backlinks.slice(0, 10).map((b) => (
                  <li key={`${b.competitor}-${b.referringDomain}`} className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      <span className="font-semibold text-gray-900">{b.referringDomain}</span>
                      <span className="text-gray-400"> → {b.competitor}</span>
                    </span>
                    <span className="text-gray-500 shrink-0">
                      {b.nordicFinance && <span className="text-[#0F3F34] mr-2">nordisk økonomi</span>}
                      rank {b.domainRank ?? "–"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {missingTiers.length > 0 && (
            <div className="space-y-2 pt-3 border-t border-gray-100">
              <h4 className="font-bold text-gray-900 text-sm">Beløbstrin flere konkurrenter har, men vi ikke har</h4>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {missingTiers.map((t) => (
                  <span key={t.amount} className="px-2 py-0.5 rounded-md border border-gray-200 bg-gray-50 text-gray-700">
                    {t.amount.toLocaleString("da-DK")} kr · {t.competitors}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
